import { AufnahmePunktDocument } from '../../data-model';
import { hatGrubenbild } from './grubenbild-abschnitte';
import { grubenbildStand } from './grubenbild-daten';
import { zeichneGrubenbild } from './grubenbild-zeichnen';

/**
 * Das Grubenbild als Rasterbild - fuer den PDF-Export.
 *
 * Das SVG entsteht fuer den Bildschirm (`zeichneGrubenbild`) und verlaesst sich
 * dort auf den Stil des Bauteils (`grubenbild.component.css`): Die Flaeche zum
 * Aufleuchten einer Schicht ist per Stil unsichtbar, der Grund hinter einer
 * Beschriftung hell, und die Schrift erbt das Bauteil von der Seite. Als
 * eigenstaendiges Bild geladen kennt das SVG keinen Bauteil-Stil - die Regeln
 * muessen MIT hinein, sonst laege ueber jeder Schicht ein weisses Rechteck,
 * hinter jeder Beschriftung ein schwarzes, und die Schrift waere die
 * Serifenschrift des Browsers.
 *
 * jsPDF kann kein SVG einbetten; deshalb der Weg ueber ein Bild-Element und
 * eine Zeichenflaeche (canvas). Beides gibt es nur im Browser - in den Tests
 * (jsdom) laesst sich nur der SVG-Text pruefen, das Rastern ist im echten
 * Chromium angesehen (25.09.2026).
 */

/**
 * Die Klassen-Regeln aus `grubenbild.component.css`, die das Bild braucht.
 * `grubenbild-als-bild.spec.ts` haelt beide Fassungen deckungsgleich.
 */
export const GRUBENBILD_SVG_REGELN: Readonly<Record<string, string>> = {
	'.gb-textgrund': 'fill: #fbfaf7',
	'.gb-aufleuchten': 'fill: #fff; opacity: 0',
};

/** Dieselbe Schriftreihe wie die der Seite (Bootstrap), damit das Bild aussieht wie im Reiter. */
export const GRUBENBILD_SCHRIFT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/**
 * Wie fein gerastert wird: Bildpunkte je SVG-Einheit. Das Bild steht im PDF
 * rund 175 mm breit; bei rund 700 Einheiten Breite ergibt Faktor 2 etwa
 * 200 Punkte je Zoll - scharf genug fuer die Beschriftung, ohne dass jede
 * Seite mehrere Megabyte wiegt.
 */
export const RASTER_FAKTOR = 2;

/**
 * JPEG statt PNG: Das Bild besteht aus tausenden Koernern und Flecken, die
 * ein PNG kaum zusammendrueckt. Gemessen (Chromium, 25.09.2026, Grube mit
 * fuenf Horizonten, Auflage, Baendern und Wasserstand, 654 x 719 Einheiten):
 * PNG x2 = 918 KB, JPEG x2 bei 0,88 = 244 KB, JPEG x2 bei 0,80 = 185 KB,
 * JPEG x3 bei 0,88 = 435 KB, PNG x3 = 1,6 MB. Das JPEG x2 bei 0,88 war im
 * Vergleich neben dem Bildschirm-SVG nicht zu unterscheiden.
 */
export const RASTER_FORMAT: RasterFormat = 'JPEG';
export const RASTER_QUALITAET = 0.88;
/** Der Grund des Rahmens im Reiter - JPEG kennt keine Durchsicht. */
export const RASTER_HINTERGRUND = '#ffffff';

export type RasterFormat = 'PNG' | 'JPEG';

export interface SvgText {
	/** Das vollstaendige SVG-Dokument als Text, mit den Stilregeln darin. */
	text: string;
	/** Natuerliche Groesse in SVG-Einheiten (aus `width`/`height`). */
	breite: number;
	hoehe: number;
}

export interface RasterBild {
	dataUrl: string;
	/** Natuerliche Groesse in SVG-Einheiten - fuers Seitenverhaeltnis im PDF. */
	breite: number;
	hoehe: number;
	format: RasterFormat;
}

export interface RasterOptionen {
	faktor?: number;
	format?: RasterFormat;
	/** Nur bei JPEG: 0 bis 1. */
	qualitaet?: number;
	hintergrund?: string;
}

/** Der `<style>`-Inhalt, der ins SVG kommt. */
export function grubenbildStilText(): string {
	const regeln = Object.entries(GRUBENBILD_SVG_REGELN).map(([selektor, regel]) => `${selektor}{${regel}}`).join('');
	return `svg{font-family:${GRUBENBILD_SCHRIFT}}${regeln}`;
}

/**
 * Macht aus dem Bildschirm-SVG ein eigenstaendiges SVG-Dokument: Namensraum,
 * die Stilregeln des Bauteils, und ohne die Bildschirm-Breite (`max-width`).
 * Das Original bleibt unberuehrt - es koennte gerade im Reiter stehen.
 */
export function grubenbildSvgText(svg: SVGElement): SvgText {
	const kopie = svg.cloneNode(true) as SVGElement;
	kopie.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
	kopie.removeAttribute('style');
	const stil = kopie.ownerDocument.createElementNS('http://www.w3.org/2000/svg', 'style');
	stil.textContent = grubenbildStilText();
	kopie.insertBefore(stil, kopie.firstChild);
	return {
		text: new XMLSerializer().serializeToString(kopie),
		breite: Number(kopie.getAttribute('width')),
		hoehe: Number(kopie.getAttribute('height')),
	};
}

/**
 * Rastert das SVG ueber ein Bild-Element und eine Zeichenflaeche. Braucht den
 * Browser; scheitert das Laden, kommt ein Fehler mit Klartext.
 */
export function svgZuBild(svg: SvgText, opt: RasterOptionen = {}): Promise<RasterBild> {
	const faktor = opt.faktor ?? RASTER_FAKTOR;
	const format = opt.format ?? RASTER_FORMAT;
	const qualitaet = opt.qualitaet ?? RASTER_QUALITAET;
	const hintergrund = opt.hintergrund ?? RASTER_HINTERGRUND;
	if (!(svg.breite > 0 && svg.hoehe > 0)) {
		return Promise.reject(new Error('Das Grubenbild hat keine Groesse.'));
	}
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			try {
				const flaeche = document.createElement('canvas');
				flaeche.width = Math.round(svg.breite * faktor);
				flaeche.height = Math.round(svg.hoehe * faktor);
				const ctx = flaeche.getContext('2d');
				if (!ctx) throw new Error('Keine Zeichenflaeche fuer das Grubenbild.');
				ctx.fillStyle = hintergrund;
				ctx.fillRect(0, 0, flaeche.width, flaeche.height);
				ctx.drawImage(img, 0, 0, flaeche.width, flaeche.height);
				const mime = format === 'JPEG' ? 'image/jpeg' : 'image/png';
				resolve({ dataUrl: flaeche.toDataURL(mime, qualitaet), breite: svg.breite, hoehe: svg.hoehe, format });
			} catch (e) {
				reject(e);
			}
		};
		img.onerror = () => reject(new Error('Das Grubenbild liess sich nicht als Bild laden.'));
		// Daten-Adresse statt Blob-Adresse: nichts freizugeben, und in jedem
		// Browser ohne Nebenwirkung auf die Zeichenflaeche.
		img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg.text);
	});
}

/**
 * Das Grubenbild eines Punktes als Rasterbild, so wie es der Reiter zeigt -
 * alle Ebenen an, nichts gewaehlt. `null`, wenn der Punkt nichts traegt, das
 * sich zeichnen laesst (dann gibt es auch keine Seite dafuer).
 *
 * @param datum Der Tag der Aufnahme fuer die Zeile am Wasserstand, oder nichts.
 */
export async function grubenbildAlsBild(
	bp: AufnahmePunktDocument, datum: string | null, opt: RasterOptionen = {},
): Promise<RasterBild | null> {
	if (!hatGrubenbild(bp)) return null;
	const stand = grubenbildStand(bp, datum);
	const svg = zeichneGrubenbild(stand.daten, {
		gewaehlt: null,
		substratgrenzen: true,
		textur: true,
		beiWahl: () => undefined,
		beiZeigen: () => undefined,
	});
	return svgZuBild(grubenbildSvgText(svg), opt);
}
